/**
 * @fileoverview Session timeout management component
 * 
 * This component monitors user activity and automatically logs out users after
 * a period of inactivity to enhance security. When a timeout occurs, it displays
 * a modal notification and performs a secure logout through Okta authentication.
 * 
 * The timeout duration is configured through the REACT_APP_SESSION_TIME_OUT_MILLIS
 * environment variable.
 */

import React, { useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Modal, ModalSize, Button, ButtonVariant, IconName, ButtonIconAlign } from '@eightfold.ai/octuple';
import { useOktaAuth } from '@okta/okta-react';
import { useLocation } from 'react-router-dom';
import { actionButtonStyle } from './commons/commonStyles';

/**
 * Modal content component for the session timeout notification
 * 
 * @param {Object} props - Component properties
 * @param {Function} props.closeModal - Function to close the modal and trigger logout
 * @returns {JSX.Element} Rendered modal content with message and continue button
 */
const ModalBody = (props: any) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ paddingBottom: '20px' }}>
        {props?.message}
      </div>
      <div style={{ display: 'flex', alignSelf: 'flex-end' }}>
        <Button
          text="Continue"
          variant={ButtonVariant.Secondary}
          classNames="actionButton"
          style={actionButtonStyle}
          onClick={() => {
            props?.closeModal();
          }}
          iconProps={{path: IconName.mdiArrowRight }}
          alignIcon={ButtonIconAlign.Right}
        />
      </div>
    </div>
  )
}

/**
 * Session timeout monitoring component that tracks user activity
 * 
 * This component uses the react-idle-timer library to monitor user activity
 * and automatically display a session timeout modal when inactivity reaches
 * the configured timeout threshold. When the user acknowledges the timeout,
 * they are securely logged out of the application.
 *
 * @returns {JSX.Element} Modal component that appears on session timeout
 */
function IdleTimer() {

  const [showModal, setShowModal] = useState(false);
  const timeOut: any = process.env.REACT_APP_SESSION_TIME_OUT_MILLIS;
  const { oktaAuth } = useOktaAuth();
  const { pathname } = useLocation();
  const isAssessmentPage = pathname.startsWith('/assessment/') || pathname.startsWith('/business-impact/');
  const sessionExpiredMessage = isAssessmentPage
    ? 'Your session has expired due to inactivity. Please log in again to continue your assessment. Your progress up to your last saved answer has been preserved.'
    : 'Your session has expired due to inactivity. Please log in again.';
  /**
   * Handles user logout by clearing session storage and signing out of Okta
   * 
   * This function ensures that all authentication state is properly cleared
   * when a user is logged out due to session timeout. It clears local session
   * storage and uses the Okta authentication API to perform a secure logout.
   * If Okta auth is unavailable, it falls back to a page reload.
   */
  const logoutUser = () => {
    sessionStorage.clear();
    if (oktaAuth) {
      oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin
      });
    } else {
      window.location.reload();
    }
  };

  /**
   * Callback triggered when the user becomes idle
   * 
   * This function shows the session timeout modal when the user
   * has been inactive for the duration specified in the timeout configuration.
   */
  const onIdle = () => {
    setShowModal(true);
  };

  /**
   * Handles the modal close action
   * 
   * When the user acknowledges the session timeout by clicking continue,
   * this function logs them out and hides the modal.
   */
  const closeModal = () => {
    logoutUser();
    setShowModal(false);
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  /**
   * Configure and initialize the idle timer with activity monitoring
   * 
   * This hook tracks user interactions with the page to determine inactivity.
   * When the configured timeout is reached, it triggers the onIdle callback.
   * 
   * Note: The eslint-disable comment is necessary because the hook is conditionally
   * used within this component, which is normally against React hooks rules.
   */
  useIdleTimer({
    onIdle,                      // Callback when user becomes idle
    timeout: parseInt(timeOut),  // Timeout duration from environment variable
    promptTimeout: 0,            // No prompt before timeout
    // Events that indicate user activity to reset the idle timer
    events: ['mousemove', 'keydown', 'wheel', 'DOMMouseScroll', 'mousewheel', 'mousedown', 'touchstart', 'touchmove', 'MSPointerDown', 'MSPointerMove', 'visibilitychange'],
    immediateEvents: [],         // Events that trigger onIdle immediately
    debounce: 0,                 // Debounce delay for events
    throttle: 0,                 // Throttle delay for events
    eventsThrottle: 200,         // Throttle rate for specified events
    element: document,           // Element to monitor for activity
    startOnMount: true,          // Start timer when component mounts
    startManually: false,        // Don't require manual start
    stopOnIdle: false,           // Don't stop timer when user becomes idle
    crossTab: true,              // Sync idle state across browser tabs
    name: 'idle-timer',          // Name for identifying this timer
    syncTimers: 0,               // Timer synchronization delay
    leaderElection: false,       // Don't elect a leader for cross-tab sync
  });
  return (
    <Modal
      visible={showModal}        // Control modal visibility based on session state
      actionsClassNames="my-modal-actions-class"
      body={<ModalBody closeModal={closeModal} message={sessionExpiredMessage} />}  // Pass modal content and close callback
      bodyClassNames="my-modal-body-class"
      bodyPadding
      configContextProps={{
        noGradientContext: false,
        noThemeContext: false
      }}
      data-test-id="my-modal-test-id"
      header="Session Expired!"   // Modal title indicating session expiration
      headerClassNames="my-modal-header-class"
      modalClassNames="my-modal-class"
      modalWrapperClassNames="my-modal-wrapper-class"
      onClose={closeModal}        // Handle modal close event
      overlay                     // Show background overlay
      positionStrategy="absolute" // Position strategy for the modal
      renderContentAlways         // Always render modal content for better UX
      size={ModalSize.small}      // Use small modal size for this notification
      themeContainerId="my-modal-theme-container"
      zIndex={1000}               // Ensure modal appears above other content
    />
  );
}

/**
 * Export the IdleTimer component as the default export
 * This component should be included near the root of the application
 * to provide session timeout monitoring across all pages
 */
export default IdleTimer;
